import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { useModalDismiss } from '../../../hooks/useModalDismiss';

/**
 * Reusable confirmation/alert modal for the projects module.
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - onConfirm: () => void  (omit for alert-only mode)
 *  - title: string
 *  - message: string
 *  - confirmLabel: string (default "Confirmer")
 *  - cancelLabel: string (default "Annuler")
 *  - variant: 'danger' | 'warning' | 'success' | 'info' (default 'warning')
 *  - loading: boolean
 */
const VARIANTS = {
  danger:  { icon: faTimesCircle,         color: '#dc2626', bg: '#fef2f2', btnBg: '#dc2626' },
  warning: { icon: faExclamationTriangle, color: '#d97706', bg: '#fffbeb', btnBg: '#d97706' },
  success: { icon: faCheckCircle,         color: '#16a34a', bg: '#f0fdf4', btnBg: '#16a34a' },
  info:    { icon: faExclamationTriangle, color: '#374151', bg: '#f3f4f6', btnBg: '#374151' },
};

const ConfirmModal = ({
  isOpen, onClose, onConfirm, title, message,
  confirmLabel = 'Confirmer', cancelLabel = 'Annuler',
  variant = 'warning', loading = false,
  // Opt-in: pass false where a stray backdrop click would be disruptive. Defaults to
  // true so every existing caller keeps its current behaviour.
  dismissOnBackdrop = true,
}) => {
  const dismiss = useModalDismiss(onClose, { backdropClose: dismissOnBackdrop, enabled: isOpen });

  if (!isOpen) return null;
  const v = VARIANTS[variant] || VARIANTS.warning;
  const isAlertOnly = !onConfirm;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div {...dismiss} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} />
      <div style={{
        position: 'relative', background: '#fff', borderRadius: 12,
        padding: '28px 32px', width: 420, maxWidth: '90vw',
        boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
        animation: 'confirmModalIn 0.15s ease',
      }}>
        {/* Icon + Title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%', background: v.bg,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <FontAwesomeIcon icon={v.icon} style={{ color: v.color, fontSize: '0.9rem' }} />
          </div>
          <h5 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#111827' }}>
            {title}
          </h5>
        </div>

        {/* Message */}
        <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '0 0 22px', lineHeight: 1.5 }}>
          {message}
        </p>

        {/* Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px', borderRadius: 6, border: '1px solid #d1d5db',
              background: '#fff', color: '#374151', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer',
            }}
          >
            {isAlertOnly ? 'Fermer' : cancelLabel}
          </button>
          {!isAlertOnly && (
            <button
              onClick={onConfirm}
              disabled={loading}
              style={{
                padding: '8px 20px', borderRadius: 6, border: 'none',
                background: v.btnBg, color: '#fff', fontSize: '0.78rem', fontWeight: 600,
                cursor: 'pointer', opacity: loading ? 0.5 : 1,
              }}
            >
              {loading ? 'Chargement...' : confirmLabel}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes confirmModalIn {
          from { opacity: 0; transform: scale(0.95) translateY(-4px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ConfirmModal;
